/**
 * VanityOrgGate (TRA-829) — on <org>.trainloop.ai, validates the org slug
 * against the public /api/auth/org/{slug} endpoint BEFORE AuthGuard runs.
 * Ordering matters: AuthGuard auto-redirects to Keycloak, and an unknown
 * org's origin is never registered as a redirect URI, so gating here turns
 * "ugly Keycloak invalid_redirect_uri error" into a clean 404 page. The
 * same call lazily registers the vanity origin on the Keycloak client so
 * the upcoming login redirect is accepted.
 *
 * On canonical hosts (platform.trainloop.ai, localhost) this is a no-op.
 */

import { ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'

import { customFetch, getApiErrorStatus } from '@/api/client'
import { VANITY_ORG_SLUG } from '@/lib/vanity'
import OrgNotFoundPage from '@/pages/OrgNotFoundPage'
import LoadingScreen from '@/components/shared/LoadingScreen'

interface OrgInfo {
  slug: string
  display_name: string
}

export function VanityOrgGate({ children }: { children: ReactNode }): React.JSX.Element {
  const orgQuery = useQuery({
    queryKey: ['/api/auth/org', VANITY_ORG_SLUG],
    queryFn: () => {
      if (VANITY_ORG_SLUG === null) {
        // enabled: guards this; loud check instead of a silent `?? ''`
        throw new Error('VanityOrgGate query ran on a non-vanity host')
      }
      return customFetch<OrgInfo>(`/api/auth/org/${VANITY_ORG_SLUG}`)
    },
    enabled: VANITY_ORG_SLUG !== null,
    staleTime: Infinity,
    retry: (failureCount, error) =>
      getApiErrorStatus(error) !== 404 && failureCount < 3,
  })

  if (VANITY_ORG_SLUG === null) {
    return <>{children}</>
  }

  if (orgQuery.isError) {
    if (getApiErrorStatus(orgQuery.error) === 404) {
      return <OrgNotFoundPage slug={VANITY_ORG_SLUG} />
    }
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-destructive">
          Failed to load organization: {orgQuery.error.message}
        </p>
      </div>
    )
  }

  if (!orgQuery.data) {
    return <LoadingScreen />
  }

  return <>{children}</>
}
